import { useEffect, useState } from 'react';
import Head from 'next/head';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';

import { AIProfileChip } from '@components/transfers/AIProfileChip';
import { CustomTransfers } from '@components/transfers/CustomTransfers';
import { Recommended } from '@components/transfers/Recommended';
import AIProfilePanel from '@components/manager-profile-settings';
import { client, getPostByURI } from 'apollo-client';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import ErrorHoldingMessage from '@components/error-holding-message';
import LoadingView from '@components/atoms/loading-view';
import Paywall from '@components/paywall';
import SegmentedControl from '@components/atoms/segmented-control';
import { WarningAlert } from '@components/alert';
import {
  FplLinkGate,
  FplLinkGateProfileError
} from '@components/my-team/FplLinkGate';
import { useAuth0User } from '@hooks/useAuth0User';
import { useFplLinkGate } from '@hooks/useFplLinkGate';
import { useIsAppView } from '@hooks/useIsAppView';
import useLoggedOutRedirect from '@hooks/useLoggedOutRedirect';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePlannerState } from '@hooks/usePlannerState';
import { PlannerApiError, isPlannerSeasonClosed } from '@api-service/planner';
import { getYoastHeadSEO, isPaidMember } from '@lib/utils';
import { useCustomTransfersStore } from '@lib/transfers/store/useCustomTransfersStore';

const RECOMMENDED_TAB_INDEX = 0;
const CUSTOM_TAB_INDEX = 1;

const RecommendedTransfersPage = ({ post }) => {
  const router = useRouter();
  const { pathname } = router;
  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, pathname);

  const { user, isLoading: userIsLoading } = useAuth0User();
  const { context } = useLeagueContext();
  const {
    hasTeam,
    isLoading: plannerIsLoading,
    isFetching: plannerIsFetching,
    isError: plannerIsError,
    error: plannerError,
    refetch: refetchPlanner
  } = usePlannerState({ enabled: Boolean(user) });

  const { isAppView } = useIsAppView();
  const hasPendingRecommendation = useCustomTransfersStore(
    (state) => state.hasPendingRecommendation
  );
  const clearPendingRecommendation = useCustomTransfersStore(
    (state) => state.clearPendingRecommendation
  );
  // Read once on mount so switching back to Recommended by hand is not
  // undone by a re-render.
  const [selectedTab, setSelectedTab] = useState<number>(
    hasPendingRecommendation ? CUSTOM_TAB_INDEX : RECOMMENDED_TAB_INDEX
  );

  // One-shot: clearing it means a later direct visit opens on Recommended
  // with no stale player out.
  useEffect(() => {
    if (!hasPendingRecommendation) return;
    setSelectedTab(CUSTOM_TAB_INDEX);
    clearPendingRecommendation();
  }, [hasPendingRecommendation, clearPendingRecommendation]);
  // The AI profile drives both tabs, so the panel and its entry point live
  // here rather than inside Recommended.
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);

  useLoggedOutRedirect({
    router,
    redirectPath: 'ai-team-rating',
    user,
    userIsLoading
  });

  const isPayingMember = isPaidMember(user);
  const { status: linkGateStatus, refetchProfile } = useFplLinkGate();
  const currentGameweek = context?.currentGameweek?.number;

  const seasonClosedFromPlanner =
    plannerError instanceof PlannerApiError &&
    isPlannerSeasonClosed(plannerError);

  const plannerErrorStatusCode =
    plannerError instanceof PlannerApiError ? plannerError.status : undefined;

  if (currentGameweek === 38 || seasonClosedFromPlanner) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <EndOfSeasonHoldingMessage
          pageTitle="AI Transfers"
          isAppView={isAppView}
        />
      </>
    );
  }

  return (
    <>
      <Head>
        {yoastHead ? (
          yoastHead
        ) : (
          <title>
            My Team FPL - Use Artificial Intelligence To Win at Fantasy Football
          </title>
        )}
      </Head>

      <div
        className="bg-white relative mx-auto pb-14 min-h-screen container px-4 md:px-8 lg:px-10"
        data-cy="ai-transfers-page"
      >
        {!userIsLoading && !isPayingMember && (
          <div className="h-screen pt-12">
            <Paywall variant="embedded" title="🔒 Unlock exclusive content" />
          </div>
        )}

        {isPayingMember && linkGateStatus === 'error' && (
          <FplLinkGateProfileError onRetry={refetchProfile} />
        )}

        {isPayingMember && linkGateStatus === 'gate' && <FplLinkGate />}

        {/* Hold the page while the link gate resolves, or while the planner
            state loads (and retries). Without this the ~8s+ retry window on a
            failing planner request rendered nothing at all. */}
        {isPayingMember &&
          (linkGateStatus === 'loading' ||
            (linkGateStatus === 'pass' && plannerIsLoading)) && (
            <div className="flex justify-center py-10">
              <LoadingView
                headerText="Loading"
                loadingText="Getting your AI transfers"
                progressBarSeconds={8}
              />
            </div>
          )}

        {/* A non-season-closed planner failure (e.g. a 503) is a real error, so
            surface a recoverable state instead of the misleading "set up your
            team" fallback below. Only take over the page when there is no
            usable team data; a failed background refetch that still has cached
            data should keep the transfers UI interactive. */}
        {isPayingMember &&
          linkGateStatus === 'pass' &&
          plannerIsError &&
          !hasTeam && (
            <ErrorHoldingMessage
              pageTitle="AI Transfers"
              statusCode={plannerErrorStatusCode}
              onRetry={refetchPlanner}
              isRetrying={plannerIsFetching}
            />
          )}

        {/* Fallback for linked members whose planner resolved with no team
            (e.g. the FPL re-import failed): point them at the Pick tab. */}
        {isPayingMember &&
          linkGateStatus === 'pass' &&
          !plannerIsLoading &&
          !plannerIsError &&
          !hasTeam && (
            <div className="pt-5">
              <WarningAlert text="Please set up your team on the Pick tab to access AI transfers" />
            </div>
          )}

        {isPayingMember && linkGateStatus === 'pass' && hasTeam && (
          <div className="pt-5 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-4">
              <SegmentedControl
                tabLabels={['Recommended', 'Custom transfers']}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                dataCyValues={[
                  'recommended-transfers-control',
                  'custom-transfers-control'
                ]}
              />
              <AIProfileChip onClick={() => setIsProfilePanelOpen(true)} />
            </div>

            {selectedTab === RECOMMENDED_TAB_INDEX ? (
              <Recommended />
            ) : (
              <CustomTransfers />
            )}

            <AIProfilePanel
              isOpen={isProfilePanelOpen}
              onClose={() => setIsProfilePanelOpen(false)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostByURI('/my-team-transfers')}
    `
  });

  const post = data.postBy;

  return {
    props: { post }
  };
};

export default RecommendedTransfersPage;
